import { useEffect, useState } from "react";
import { useAuthStore } from "@/lib/auth";
import { useTenantMe } from "@/lib/tenant-me";
import {
  useUpdateUserMe,
  useUploadUserAvatar,
  useUserMe,
} from "@/lib/user-me";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageUploader } from "@/components/v2/ImageUploader";
import { useToast } from "@/components/v2/Toast";
import { SettingsSection } from "@/components/v2/account/SettingsSection";

/**
 * `/account/profile` — name + avatar. The email field is shown
 * read-only here as a reference; changing it lives at `/account/email`.
 *
 * Sign-out lives in this leaf because it's the closest match to
 * "personal identity." It clears the access token + bounces to the
 * auth subapp.
 */
export function AccountProfile() {
  const me = useUserMe();
  const tenant = useTenantMe();
  const update = useUpdateUserMe();
  const uploadAvatar = useUploadUserAvatar();
  const toast = useToast();
  const clear = useAuthStore((s) => s.clear);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (me.data) {
      setFirstName(me.data.firstName ?? "");
      setLastName(me.data.lastName ?? "");
    }
  }, [me.data]);

  const dirty =
    firstName.trim() !== (me.data?.firstName ?? "").trim() ||
    lastName.trim() !== (me.data?.lastName ?? "").trim();
  const valid = firstName.trim().length > 0;
  const canSave = dirty && valid && !submitting;

  const onSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      await update.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
      });
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Couldn't save", (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = () => {
    clear();
    const authUrl =
      import.meta.env.VITE_AUTH_APP_URL ?? "http://localhost:5174";
    window.location.assign(`${authUrl}/sign-in`);
  };

  const role = tenant.data?.user?.role ?? "—";
  const workspaceName = tenant.data?.tenant?.name ?? "—";

  return (
    <SettingsSection
      title="Profile"
      description="Your name and avatar appear in the sidebar and on bookings."
    >
      <div className="grid gap-5">
        <div className="grid gap-1.5">
          <Label className="text-[13px] font-semibold">Avatar</Label>
          <ImageUploader
            shape="circle"
            size={80}
            label="Profile avatar"
            value={me.data?.avatarUrl}
            onUpload={(file) => uploadAvatar.mutateAsync(file)}
            helpText="Square images crop best. PNG or JPG, up to 5 MB."
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="firstName" className="text-[13px] font-semibold">
              First name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="firstName"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="lastName" className="text-[13px] font-semibold">
              Last name
            </Label>
            <Input
              id="lastName"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span className="text-xs uppercase tracking-wider rounded-full bg-muted px-2.5 py-0.5 font-semibold">
              {role}
            </span>
            <span>{workspaceName}</span>
            <button
              type="button"
              onClick={handleSignOut}
              className="text-primary hover:underline text-xs"
            >
              Sign out
            </button>
          </div>
          <Button disabled={!canSave} onClick={onSave}>
            {submitting ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </SettingsSection>
  );
}
